import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import FarmCard from "@/components/FarmCard";
import { Heart, ListChecks, Calendar, Users, Loader2 } from "lucide-react";
import { ACTIVITY_LABELS } from "@/lib/activities";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("favorites");
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      Promise.all([
        api.get("/favorites").then((r) => setFavorites(r.data)),
        api.get("/bookings/mine").then((r) => setBookings(r.data)),
      ]).finally(() => setReady(true));
    }
  }, [user]);

  if (!user || !ready) return (
    <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#4A6B53]" size={28}/></div>
  );

  const statusColor = (s) => ({
    pending: "bg-[#E4B871]/25 text-[#8B6514]",
    confirmed: "bg-[#4A6B53]/15 text-[#4A6B53]",
    cancelled: "bg-[#E5DCD0] text-[#7A7369]",
  }[s] || "bg-[#E5DCD0] text-[#7A7369]");

  const statusLabel = (s) => ({ pending: "In attesa", confirmed: "Confermata", cancelled: "Annullata" }[s] || s);

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-10 py-10 md:py-14">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C8795A] mb-2">Area personale</div>
          <h1 className="font-display font-black text-4xl md:text-5xl text-[#2C312E]">Ciao, {user.name?.split(" ")[0]}</h1>
          <p className="mt-2 text-[#58625B]">{user.user_type === "scuola" ? "Scuola" : "Famiglia"} · {user.email}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <StatCard icon={Heart} label="Preferiti" value={favorites.length} color="#C8795A"/>
        <StatCard icon={ListChecks} label="Richieste totali" value={bookings.length} color="#4A6B53"/>
        <StatCard icon={Calendar} label="In attesa" value={bookings.filter((b) => b.status === "pending").length} color="#E4B871"/>
      </div>

      <div className="inline-flex rounded-full bg-white border-2 border-[#E5DCD0] p-1 mb-8">
        <TabBtn active={tab === "favorites"} onClick={() => setTab("favorites")} testid="tab-favorites">
          <Heart size={14}/> Preferiti
        </TabBtn>
        <TabBtn active={tab === "bookings"} onClick={() => setTab("bookings")} testid="tab-bookings">
          <ListChecks size={14}/> Richieste di visita
        </TabBtn>
      </div>

      {tab === "favorites" && (
        favorites.length === 0 ? (
          <EmptyState title="Nessun preferito" msg="Aggiungi fattorie ai preferiti dalla lista o dalla scheda dettaglio."/>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {favorites.map((f) => <FarmCard key={f.id} farm={f}/>)}
          </div>
        )
      )}

      {tab === "bookings" && (
        bookings.length === 0 ? (
          <EmptyState title="Nessuna richiesta" msg="Le tue richieste di visita appariranno qui."/>
        ) : (
          <div className="space-y-3">
            {bookings.map((b) => (
              <div key={b.id} className="rounded-3xl bg-white border border-[#E5DCD0] p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid={`booking-${b.id}`}>
                <div className="flex-1">
                  <div className="font-display font-black text-lg text-[#2C312E]">{b.company_name}</div>
                  <div className="text-sm text-[#58625B] mt-1 flex flex-wrap gap-4">
                    <span className="flex items-center gap-1.5"><Calendar size={13}/> {b.preferred_date}</span>
                    <span className="flex items-center gap-1.5"><Users size={13}/> {b.group_size} {b.visitor_type === "scuola" ? "alunni" : "persone"}</span>
                  </div>
                  {b.activities?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {b.activities.map((a) => <span key={a} className="rounded-full bg-[#F2EBE1] text-[#4A6B53] px-2 py-0.5 text-[11px] font-bold">{ACTIVITY_LABELS[a] || a}</span>)}
                    </div>
                  )}
                  {b.message && <div className="mt-2 text-sm text-[#58625B] italic">« {b.message} »</div>}
                </div>
                <span className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-bold ${statusColor(b.status)}`}>{statusLabel(b.status)}</span>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-3xl bg-white border border-[#E5DCD0] p-6 flex items-center gap-4">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white" style={{ background: color }}>
        <Icon size={22}/>
      </div>
      <div>
        <div className="font-display font-black text-3xl text-[#2C312E]">{value}</div>
        <div className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">{label}</div>
      </div>
    </div>
  );
}
function TabBtn({ active, onClick, children, testid }) {
  return <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${active ? "bg-[#4A6B53] text-white" : "text-[#58625B] hover:text-[#2C312E]"}`}>{children}</button>;
}
function EmptyState({ title, msg }) {
  return <div className="rounded-3xl border-2 border-dashed border-[#E5DCD0] p-10 text-center"><p className="font-display font-bold text-[#2C312E] mb-1">{title}</p><p className="text-sm text-[#58625B]">{msg}</p></div>;
}
